import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastHandlerService } from '../../../../shared/services/toast-handler.service';
import { NbDialogService } from '@nebular/theme';
import { BackendPaginationResponse } from '../../../../shared/models/backend-pagination-response';
import { environment } from '../../../../../environments/environment';
import { catchError, map } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { Place } from '../models/place';
import { TranslateService } from '@ngx-translate/core';
import {PvModalDialogService} from '../../../../shared/services/pv-modal-dialog.service';
import { BackendFilteringObject } from 'src/app/shared/models/grid-config.interface';
import { ExportCsvService } from 'src/app/shared/services/export-csv.service';

@Injectable({
  providedIn: 'root'
})
export class AdminPlaceService {

  constructor(private http: HttpClient, private toastService: ToastHandlerService,
              private translateService: TranslateService,
              private pvDialogService: PvModalDialogService,
              private exportService: ExportCsvService,
              private dialogService: NbDialogService) {
  }

  public getPlaces(filteringObj: BackendFilteringObject): Promise<BackendPaginationResponse<Place>> {
    return this.http.get(`${environment.backendUrl}admin/places?paginate=true&${filteringObj.getBackendFiltering()}`)
      .pipe(
        map((res: any) => res),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public getPlacesByEntity(entityId: string, placeId: string, page: number, limit: number, search?: string): Promise<Place[]> {
    const params = {
      entityId,
      placeId,
      paginate: 'true',
      page: page.toString(),
      limit: limit.toString(),
      search: search || ''
    };
    return this.http.get(`${environment.backendUrl}admin/places-for-order`, {
      params
    })
      .pipe(
        map((res: any) => res.data),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public getPlace(placeId: string): Promise<Place> {
    return this.http.get(`${environment.backendUrl}admin/places/${placeId}`)
      .pipe(
        map((res: any) => res.data),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public createPlace(place: Place, showSuccessToast = true): Promise<Place> {
    return this.http.post(`${environment.backendUrl}admin/places`,
      place)
      .pipe(
        map((res: any) => {
          if (showSuccessToast) {
            this.toastService.showSuccessToast(
              this.translateService.instant('admin.modules.places.services.place_created_success')
            );
          }
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public updatePlace(placeId: string, place: Place, showSuccessToast = true): Promise<Place> {
    return this.http.put(`${environment.backendUrl}admin/places/${placeId}`,
      place)
      .pipe(
        map((res: any) => {
          if (showSuccessToast) {
            this.toastService.showSuccessToast(
              this.translateService.instant('admin.modules.places.services.place_updated_success')
            );
          }
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public deletePlace(placeId: string): Promise<Place> {
    return this.http.delete(`${environment.backendUrl}admin/places/${placeId}`)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('admin.modules.places.services.place_deleted_success')
          );
          return res.data;
        }),
        catchError(err => {
          return this.manageError(err, 'admin.modules.places.services.place_deleted_error');
        })
      ).toPromise();
  }

  public createRestitutionCenters(entityId: string): Promise<any> {
    return this.http.post(`${environment.backendUrl}places/create-restitution-centers/${entityId}`, {})
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('admin.modules.places.services.restitution_centers_created_success')
          );
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  private manageError(err, i18nErrKey: string) {
    if (err.error.errorCode === 'address_used') {
      this.pvDialogService.openAddressUsedModal();
    } else {
      this.toastService.showErrorToasts(
        this.translateService.instant(i18nErrKey)
      );
      return throwError(err);
    }
  }

  public exportPlacesPageData(localData: any[], columns?: any[]): Promise<any> {
    try {
      let formattedExport;
      formattedExport = this.exportPlaceParser(localData);
      this.exportService.export(formattedExport);
      return Promise.resolve(formattedExport);
    } catch (error) {
      this.toastService.showErrorToasts(
        this.translateService.instant('admin.modules.places.services.export_error')
      );
      return Promise.reject(error);
    }
  }

  public translateCategory (category: string) : string {
    return this.translateService.instant('admin.modules.places.place_grid.' + category);
  }

  private exportPlaceParser(dataToExport: any[]): any[] {
    const formattedExport = [];
    dataToExport.forEach(row => {
      formattedExport.push({
        'Client': row.customer ? row.customer.name : '',
        'Catégorie': this.translateCategory(row.category),
        'Nom': row.name || '',
        'Adresses': this.formatAdresses(row.addresses),
        'Contacts': this.formatContacts(row.contacts),
        'Global': row.global ? 'Oui' : 'Non'
      });
    });
    return formattedExport;
  }

  public formatContacts(contacts: any[]): string {
    if (!contacts || contacts.length === 0) {
      return '-';
    }

    if (contacts.length === 1) {
      return `${contacts[0].firstName} ${contacts[0].lastName}`;
    }

    return contacts.length.toString();
  }

  public formatAdresses(addresses: any[]): string {
    if (!addresses || addresses.length === 0) {
      return null;
    }

    if (addresses.length === 1) {
      return addresses[0].label || '';
    }

    return addresses.length.toString();
  }
}
